import React, { useState, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import DirectorioEmpleados from './components/DirectorioEmpleados.js';
import CalendarioTurnos from './components/CalendarioTurnos.js';
import ConfiguracionAforos from './components/ConfiguracionAforos.js';
import ReportesHorarios from './components/ReportesHorarios.js';
import NovedadesHorarios from './components/NovedadesHorarios.js'; 

function PantallaDeEmergencia({ error, resetErrorBoundary }) {
  return (
    <div className="p-10 bg-red-50 flex flex-col items-center justify-center text-center rounded-[34px] border border-red-200 min-h-[50vh]">
      <span className="text-6xl mb-4">⚠️</span>
      <h2 className="text-red-600 font-black text-2xl mb-2">¡Ups! Cortocircuito en el módulo.</h2>
      <p className="text-gray-600 text-sm mb-6 max-w-md">
        Ocurrió un error inesperado en esta pantalla: <br/> 
        <code className="bg-white px-2 py-1 rounded mt-2 block border text-red-500 font-mono text-xs">{error.message}</code>
      </p>
      <button 
        onClick={resetErrorBoundary} 
        className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-all shadow-md"
      >
        Reiniciar Pantalla
      </button>
    </div>
  );
}

export default function GestorHorarios({ usuario, onBackToMenu }) {
  const [vistaActiva, setVistaActiva] = useState('menu_principal');

  // ==========================================
  // LÓGICA DE CONTROL ABAC (DEFENSA EN CAPAS)
  // ==========================================
  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed; 
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  const cargoUpper = String(usuario?.cargo || "").toUpperCase().trim();
  const esAdminGlobal = cargoUpper === "SUPER_ADMIN" || cargoUpper === "GERENTE";

  const checkPermiso = (nombreSubmodulo) => {
    if (esAdminGlobal) return true;
    return permisosABAC[nombreSubmodulo] !== undefined;
  };

  // Validaciones exactas con los nombres de la base de datos (sub_formularios)
  const tieneDirectorio    = checkPermiso('Directorio de Empleados');
  const tieneCalendario    = checkPermiso('Calendario de Horarios');
  const tieneNovedades     = checkPermiso('Novedades y Horas Extras'); // El que acabamos de insertar
  const tieneConfiguracion = checkPermiso('Configuración de Aforos');
  const tieneReportes      = checkPermiso('Reportes y Exportación');

  const renderizarMenu = () => (
    <div className="animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-400/20 pb-6 mb-8 gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Gestión de Horarios</h2>
          <p className="text-xs font-bold text-gray-500 mt-1 uppercase tracking-wider">Sistema Inteligente de Turnos y Aforos</p>
        </div>
        <button 
          onClick={onBackToMenu}
          className="px-6 py-3 rounded-xl bg-white/70 hover:bg-white text-gray-800 text-xs font-bold transition-all border border-gray-200 shadow-sm"
        >
          Volver al Panel Central
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
        
        {tieneDirectorio && (
          <button onClick={() => setVistaActiva('directorio')} className="bg-white/70 backdrop-blur-md p-6 rounded-[28px] shadow-sm border border-white hover:shadow-xl hover:-translate-y-2 transition-all text-left group">
            <span className="text-5xl block mb-4 group-hover:scale-110 transition-transform">👥</span>
            <h3 className="font-black text-gray-800">Directorio</h3>
            <p className="text-xs text-gray-500 mt-2">Personal y códigos</p>
          </button>
        )}
        
        {tieneCalendario && (
          <button onClick={() => setVistaActiva('calendario')} className="bg-white/70 backdrop-blur-md p-6 rounded-[28px] shadow-sm border border-white hover:shadow-xl hover:-translate-y-2 transition-all text-left group">
            <span className="text-5xl block mb-4 group-hover:scale-110 transition-transform">📅</span>
            <h3 className="font-black text-gray-800">Calendario</h3>
            <p className="text-xs text-gray-500 mt-2">Asignación Drag & Drop</p>
          </button>
        )}

        {tieneNovedades && (
          <button onClick={() => setVistaActiva('novedades')} className="bg-white/70 backdrop-blur-md p-6 rounded-[28px] shadow-sm border border-white hover:shadow-xl hover:-translate-y-2 transition-all text-left group">
            <span className="text-5xl block mb-4 group-hover:scale-110 transition-transform">🏥</span>
            <h3 className="font-black text-gray-800">Novedades</h3>
            <p className="text-xs text-gray-500 mt-2">Ausencias y horas extras</p>
          </button>
        )}
        
        {tieneConfiguracion && (
          <button onClick={() => setVistaActiva('configuracion')} className="bg-white/70 backdrop-blur-md p-6 rounded-[28px] shadow-sm border border-white hover:shadow-xl hover:-translate-y-2 transition-all text-left group">
            <span className="text-5xl block mb-4 group-hover:scale-110 transition-transform">⚙️</span>
            <h3 className="font-black text-gray-800">Configuración</h3>
            <p className="text-xs text-gray-500 mt-2">Aforos y Sedes</p>
          </button>
        )}

        {tieneReportes && (
          <button onClick={() => setVistaActiva('reportes')} className="bg-white/70 backdrop-blur-md p-6 rounded-[28px] shadow-sm border border-white hover:shadow-xl hover:-translate-y-2 transition-all text-left group">
            <span className="text-5xl block mb-4 group-hover:scale-110 transition-transform">📊</span>
            <h3 className="font-black text-gray-800">Reportes</h3>
            <p className="text-xs text-gray-500 mt-2">Gerencia y Exportación</p>
          </button>
        )}

      </div>
      
      {(!tieneDirectorio && !tieneCalendario && !tieneNovedades && !tieneConfiguracion && !tieneReportes) && (
        <div className="text-center p-10 bg-white/50 rounded-3xl border border-dashed border-gray-300">
          <span className="text-4xl block mb-3">🔒</span>
          <p className="text-gray-600 font-bold text-sm">No tienes permisos asignados en este módulo.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full min-h-screen p-4 sm:p-8 relative z-10">
      <div className="bg-white/40 backdrop-blur-[40px] rounded-[34px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-6 sm:p-10 max-w-7xl mx-auto border border-white/60">
        
        <ErrorBoundary 
          FallbackComponent={PantallaDeEmergencia}
          onReset={() => setVistaActiva('menu_principal')}
        >
          {vistaActiva === 'menu_principal' && renderizarMenu()}
          
          {vistaActiva === 'directorio' && tieneDirectorio && (
            <DirectorioEmpleados usuario={usuario} onVolver={() => setVistaActiva('menu_principal')} />
          )}
          
          {vistaActiva === 'calendario' && tieneCalendario && (
            <CalendarioTurnos usuario={usuario} onVolver={() => setVistaActiva('menu_principal')} />
          )}

          {vistaActiva === 'novedades' && tieneNovedades && (
            <NovedadesHorarios usuario={usuario} onVolver={() => setVistaActiva('menu_principal')} />
          )}
          
          {vistaActiva === 'configuracion' && tieneConfiguracion && (
            <ConfiguracionAforos usuario={usuario} onVolver={() => setVistaActiva('menu_principal')} />
          )}
          
          {vistaActiva === 'reportes' && tieneReportes && (
            <ReportesHorarios usuario={usuario} onVolver={() => setVistaActiva('menu_principal')} />
          )}
        </ErrorBoundary>

      </div>
    </div>
  );
}